import React from 'react'

import { Box, Button, Card, CardContent, CardHeader, Tab, Tabs } from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { exportFile } from 'actions/app-actions'
import {
  CampaignBanners,
  CampaignDetails,
  CampaignHealth,
  CampaignNotes,
  CampaignPayment,
  CampaignReportButton,
  CampaignTargets,
} from 'components/campaign'
import LoadingView from 'components/LoadingView'
import { useAppDispatch } from 'hooks/useAppDispatch'
import useCampaign from 'hooks/useCampaign'

const Campaign = () => {
  const { id, tab: routeTab } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { data, isLoading, error } = useCampaign(id)
  const tab = routeTab || 'details'

  const details = data?.details

  const handleTabChange = (_e: any, newTab: any) => {
    navigate(UrlProvider.getUrl('campaign.tab', { id, tab: newTab }))
  }

  const handleReportFormatSelect = (format: any) => {
    if (!details) return
    const url = `${__URL_API__}/customers/${details.customerId}/campaigns/${id}/report/${format}?`
    dispatch(exportFile({ url }))
  }

  return (
    <Card>
      <CardHeader
        action={
          <div>
            <CampaignReportButton onFormatSelect={handleReportFormatSelect} />
            <Button component={Link} to={UrlProvider.getUrl('campaign.edit', { id })}>
              Edit
            </Button>
            <Button color="secondary" disabled>
              Delete
            </Button>
          </div>
        }
        subheader={
          <Box
            component={Link}
            sx={{ color: 'text.secondary' }}
            to={UrlProvider.getUrl('customer', { id: details?.customerId, tab: '' })}>
            {details?.customerName}
          </Box>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* 'campaigns/' is not a valid URL key (should be 'campaigns') — pre-existing bug, preserved. */}
            <Box
              component={Link}
              sx={{
                mr: '10px',
                color: 'text.secondary',
                fontSize: 14,
                textDecoration: 'none',
                textTransform: 'uppercase',
                '&:hover': { textDecoration: 'underline' },
              }}
              to={UrlProvider.getUrl('campaigns/' as any)}>
              campaigns
            </Box>
            {`/ ${details?.title || ''}`}
          </Box>
        }
      />

      <CardContent>
        <Tabs onChange={handleTabChange} sx={{ mb: '2rem' }} value={tab}>
          <Tab label="Details" value="details" />
          <Tab label="Targets" value="targets" />
          <Tab label="Banners" value="banners" />
          <Tab label="Payment" value="payment" />
          <Tab label="Notes" value="notes" />
          <Tab label="Health" value="health" />
        </Tabs>
        <LoadingView error={error ? 'Error occurred when loading campaign data' : undefined} loading={isLoading}>
          {tab === 'details' && data && <CampaignDetails campaign={data} campaignId={id ?? ''} />}
          {tab === 'targets' && <CampaignTargets />}
          {tab === 'banners' && <CampaignBanners customerId={details?.customerId ?? 0} />}
          {tab === 'notes' && <CampaignNotes />}
          {tab === 'payment' && <CampaignPayment />}
          {tab === 'health' && <CampaignHealth />}
        </LoadingView>
      </CardContent>
    </Card>
  )
}

export default Campaign
